import React, { useRef, useState } from "react";
import { Upload, X, Loader2, FileText, Image as ImageIcon } from "lucide-react";
import { base44 } from "@/api/base44Client";

const MAX_FILES = 10;
const MAX_SIZE = 25 * 1024 * 1024; // 25MB

export default function FileUpload({ attachments, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");

  const handleFiles = async (files) => {
    setError("");
    const fileArr = Array.from(files);
    const remaining = MAX_FILES - attachments.length;
    const toUpload = fileArr.slice(0, remaining);

    if (fileArr.length > remaining) {
      setError(`Maximum ${MAX_FILES} files. Only the first ${remaining} were added.`);
    }

    for (const file of toUpload) {
      if (file.size > MAX_SIZE) {
        setError(`${file.name} exceeds the 25MB limit.`);
        continue;
      }
      setUploading((prev) => [...prev, file.name]);
      try {
        const result = await base44.integrations.Core.UploadFile({ file });
        onChange([
          ...attachments,
          { name: file.name, url: result.file_url, type: file.type },
        ]);
      } catch {
        setError(`Failed to upload ${file.name}.`);
      }
      setUploading((prev) => prev.filter((n) => n !== file.name));
    }
  };

  const removeFile = (index) => {
    onChange(attachments.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={`cursor-pointer rounded-squircle border-2 border-dashed p-6 text-center transition-colors ${
          dragOver
            ? "border-chart-3 bg-chart-3/10"
            : "border-accent-foreground/20 hover:border-accent-foreground/40"
        }`}
      >
        <Upload
          size={24}
          className={`mx-auto mb-2 transition-colors ${
            uploading.length > 0
              ? "text-chart-3 animate-pulse"
              : "text-accent-foreground/70"
          }`}
        />
        <p className="text-sm font-medium text-accent-foreground/80">
          {uploading.length > 0 ? "Uploading..." : "Add files"}
        </p>
        <p className="text-xs text-accent-foreground/70 mt-1">
          Drag images, screenshots, or PDFs — or click to browse
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,.pdf"
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {error && <p className="text-xs text-red-300 mt-2">{error}</p>}

      {(attachments.length > 0 || uploading.length > 0) && (
        <div className="mt-3 space-y-2">
          {uploading.map((name) => (
            <div
              key={name}
              className="flex items-center gap-2 text-sm text-accent-foreground/70"
            >
              <Loader2 size={14} className="animate-spin" />
              <span>{name}</span>
            </div>
          ))}
          {attachments.map((file, i) => (
            <div
              key={i}
              className="flex items-center gap-2 bg-accent-foreground/10 rounded-lg px-3 py-2"
            >
              {file.type?.startsWith("image/") ? (
                <ImageIcon
                  size={16}
                  className="text-accent-foreground/60 shrink-0"
                />
              ) : (
                <FileText
                  size={16}
                  className="text-accent-foreground/60 shrink-0"
                />
              )}
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-accent-foreground/80 hover:text-accent-foreground truncate flex-1"
              >
                {file.name}
              </a>
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="text-accent-foreground/70 hover:text-accent-foreground shrink-0"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}